import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SupplierAuthProvider, useSupplierAuth } from "./context/SupplierAuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import SupplierDetail from "./pages/SupplierDetail";
import SupplierForm from "./pages/SupplierForm";
import Documents from "./pages/Documents";
import Compliance from "./pages/Compliance";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import Analytics from "./pages/Analytics";

// Supplier Portal Pages
import SupplierLogin from "./pages/supplier-portal/SupplierLogin";
import SupplierSetPassword from "./pages/supplier-portal/SupplierSetPassword";
import SupplierDashboard from "./pages/supplier-portal/SupplierDashboard";
import SupplierProfile from "./pages/supplier-portal/SupplierProfile";
import SupplierDocuments from "./pages/supplier-portal/SupplierDocuments";

// Components
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Supplier Protected Route Component
const SupplierProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSupplierAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/supplier-portal/login" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SupplierAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/register" element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } />
              <Route path="/forgot-password" element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } />
              <Route path="/reset-password" element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } />
              
              {/* Supplier Portal Public Routes */}
              <Route path="/supplier-portal/login" element={<SupplierLogin />} />
              <Route path="/supplier-portal/set-password" element={<SupplierSetPassword />} />
              
              {/* Supplier Portal Protected Routes */}
              <Route path="/supplier-portal/dashboard" element={
                <SupplierProtectedRoute>
                  <SupplierDashboard />
                </SupplierProtectedRoute>
              } />
              <Route path="/supplier-portal/profile" element={
                <SupplierProtectedRoute>
                  <SupplierProfile />
                </SupplierProtectedRoute>
              } />
              <Route path="/supplier-portal/documents" element={
                <SupplierProtectedRoute>
                  <SupplierDocuments />
                </SupplierProtectedRoute>
              } />
              <Route path="/supplier-portal" element={<Navigate to="/supplier-portal/dashboard" replace />} />
              
              {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers" element={
              <ProtectedRoute>
                <Suppliers />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers/new" element={
              <ProtectedRoute>
                <SupplierForm />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers/:id" element={
              <ProtectedRoute>
                <SupplierDetail />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers/:id/edit" element={
              <ProtectedRoute>
                <SupplierForm />
              </ProtectedRoute>
            } />
            
            <Route path="/documents" element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            } />
            
            <Route path="/compliance" element={
              <ProtectedRoute>
                <Compliance />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        </SupplierAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
